import {
  CurrentPlanInOfferingMode,
  Experiments,
  PaymentCycle,
  ProductChangeType,
  ProductFamily,
} from 'enums';
import { AppStore } from 'stores/App.store';
import { AdvancedPlansProductData } from 'types';
import { getProductFamilyIsInSale } from 'utils';

export class AdvancedPlansProductModel {
  private readonly productData: AdvancedPlansProductData;
  isProductInSale: boolean = false;
  isCurrentPlanProduct: boolean;
  isCurrentPlanSameCode: boolean;
  isCurrentPlanSameProductFamily: boolean;

  constructor(
    data: AdvancedPlansProductData,
    private readonly appStore: AppStore,
  ) {
    const {
      currentPlanInOfferingMode,
      appContext: { currentPlanDetails, isDynamicOffering, experiments },
    } = appStore;
    this.productData = data;
    this.isCurrentPlanProduct =
      currentPlanInOfferingMode ===
        CurrentPlanInOfferingMode.ProductFullMatch &&
      (data.id === currentPlanDetails!.planId ||
        (isDynamicOffering && data.guid === currentPlanDetails!.guid));

    this.isCurrentPlanSameCode =
      appStore.currentPlanInOfferingMode ===
        CurrentPlanInOfferingMode.ProductPartialMatch &&
      data.productCode === currentPlanDetails!.productCode;

    this.isCurrentPlanSameProductFamily =
      experiments[Experiments.ChangePlanProductFamilyFix] === 'true' &&
      appStore.currentPlanInOfferingMode ===
        CurrentPlanInOfferingMode.ProductPartialMatch &&
      data.productFamilyId === currentPlanDetails!.productFamilyId;

    this.isProductInSale = getProductFamilyIsInSale(data.productFamily);
  }

  private readonly findCycle = (cycle: PaymentCycle) =>
    this.productData.cycles.find((c) => c.cycleId === cycle);

  getYearlyFullPrice = () => {
    const yearly = this.findCycle(PaymentCycle.YEARLY);
    return yearly ? yearly.monthlyFullPrice : NaN;
  };

  getFirstCycleYearlyMonthly = () => {
    const yearly = this.findCycle(PaymentCycle.YEARLY);
    return yearly ? yearly.firstPeriodPriceMonthly : NaN;
  };

  getYearlyActualPrice = () => {
    const yearly = this.findCycle(PaymentCycle.YEARLY);
    return yearly ? yearly.monthlyActualPrice : NaN;
  };

  getActualPriceByCycle = (cycle: PaymentCycle) => {
    const nYearly = this.findCycle(cycle);
    return nYearly ? nYearly.monthlyActualPrice : this.getYearlyActualPrice();
  };

  getFullPriceByCycle = (cycle: PaymentCycle) => {
    const nYearly = this.findCycle(cycle);
    return nYearly ? nYearly.monthlyActualPrice : this.getYearlyFullPrice();
  };

  isSelected = () => {
    const { isChangePlan, getDefaultCurrentProductForPlanType } = this.appStore;
    return (
      getDefaultCurrentProductForPlanType.id === this.productData.id &&
      (!isChangePlan ||
        this.productData.productChangeType === ProductChangeType.Upgrade)
    );
  };

  getPriceToDisplay = () =>
    this.getFirstCycleYearlyMonthly() || this.getYearlyActualPrice();

  isPurchaseEnabled = () => {
    const { isSecondUpgrade, experiments } = this.appStore;

    const oldCheck = isSecondUpgrade
      ? this.productData.hasPurchasableCycles
      : !(this.isCurrentPlanProduct || this.isCurrentPlanSameProductFamily);

    const newCheck = isSecondUpgrade
      ? this.productData.hasPurchasableCycles
      : this.productData.hasPurchasableCycles &&
        !this.isCurrentPlanProduct &&
        !this.isCurrentPlanSameProductFamily;

    return experiments[Experiments.ChangeCycleCtaBugFix] === 'true'
      ? newCheck
      : oldCheck;
  };
  getId = () => this.productData.id;
  getGuid = () => this.productData.guid;
  getBaseProduct = () => this.productData;
  getCode = () => this.productData.productCode;
  getHatLabel = () => {
    const { offeringHatData, showHatFromDealer } = this.appStore;
    if (showHatFromDealer()) {
      const guid = this.getGuid();
      if (
        offeringHatData.productFamilies?.includes(
          this.productData.productFamily,
        ) ||
        (guid && offeringHatData.productIds?.includes(guid))
      ) {
        return offeringHatData.text;
      }
    } else {
      return this.productData.hatKey;
    }
  };
  getPopular = () => {
    const { offeringHatData, showHatFromDealer } = this.appStore;
    if (showHatFromDealer()) {
      const guid = this.getGuid();
      return (
        offeringHatData.productFamilies?.includes(
          this.productData.productFamily,
        ) ||
        (guid && offeringHatData.productIds?.includes(guid))
      );
    } else {
      return this.productData.popular;
    }
  };
  getType = () => this.productData.type;
  getName = () => this.productData.productName;
  getDescription = () => this.productData.description;
  shouldShowAdsNote = () => this.productData.shouldShowAdsNote;
  isEnterprise = () =>
    this.productData.productFamily === ProductFamily.Enterprise;
  getProductChangeType = () => this.productData.productChangeType;
  getDiscountPercent = () => this.productData.firstPeriodDiscount;
  getSaleDiscountPercentage = () => this.productData.saleDiscountPercentage;
  isNonPurchasable = () => this.productData.productName === 'Free';
}
